import { useState, useEffect } from 'react'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import {
  Settings,
  Shield,
  Bell,
  Globe,
  Loader2,
  Save,
} from 'lucide-react'

interface SettingValue {
  value: boolean | string | number
  description: string
  updatedAt: string
}

interface SettingsResponse {
  settings: Record<string, SettingValue>
}

export function SettingsPage() {
  const { user } = useAuthStore()
  const [settings, setSettings] = useState<Record<string, SettingValue>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    async function fetchSettings() {
      try {
        const data = await api.get<SettingsResponse>('/settings')
        setSettings(data.settings)
      } catch (err) {
        setError('Failed to load settings')
      } finally {
        setLoading(false)
      }
    }
    fetchSettings()
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      // Convert settings to the format expected by API
      const settingsToSave: Record<string, boolean | string | number> = {}
      for (const [key, setting] of Object.entries(settings)) {
        settingsToSave[key] = setting.value
      }
      await api.patch('/settings', { settings: settingsToSave })
      setSuccess('Settings saved successfully')
    } catch (err) {
      setError('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const updateSetting = (key: string, value: boolean | string | number) => {
    setSettings({
      ...settings,
      [key]: {
        ...settings[key],
        value,
      },
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  const isSuperAdmin = user?.role === 'super_admin'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Platform Settings</h1>
          <p className="text-muted-foreground">
            Configure global platform settings
          </p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save Changes
          </button>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive-foreground rounded-lg p-4 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/20 text-green-500 rounded-lg p-4 text-sm">
          {success}
        </div>
      )}

      {!isSuperAdmin && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 rounded-lg p-4 text-sm">
          You need Super Admin privileges to modify platform settings.
        </div>
      )}

      {/* General Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Globe className="h-5 w-5" />
          General
        </h3>
        <div className="space-y-4">
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.maintenance_mode?.value === true || settings.maintenance_mode?.value === 'true'}
              onChange={(e) => updateSetting('maintenance_mode', e.target.checked)}
              disabled={!isSuperAdmin}
              className="h-4 w-4 rounded border-input"
            />
            <div>
              <span className="text-sm font-medium">Maintenance Mode</span>
              <p className="text-xs text-muted-foreground">
                {settings.maintenance_mode?.description || 'Enable maintenance mode for the platform'}
              </p>
            </div>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={settings.allow_registrations?.value === true || settings.allow_registrations?.value === 'true'}
              onChange={(e) => updateSetting('allow_registrations', e.target.checked)}
              disabled={!isSuperAdmin}
              className="h-4 w-4 rounded border-input"
            />
            <div>
              <span className="text-sm font-medium">Allow Registrations</span>
              <p className="text-xs text-muted-foreground">
                {settings.allow_registrations?.description || 'Allow new tenant registrations'}
              </p>
            </div>
          </label>
          <div className="space-y-2">
            <label className="text-sm font-medium">Default Plan</label>
            <select
              value={String(settings.default_plan?.value || 'free').replace(/"/g, '')}
              onChange={(e) => updateSetting('default_plan', e.target.value)}
              disabled={!isSuperAdmin}
              className="w-full max-w-xs rounded-lg border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
            >
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
            <p className="text-xs text-muted-foreground">
              {settings.default_plan?.description || 'Default plan for new tenants'}
            </p>
          </div>
        </div>
      </div>

      {/* Limits Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Limits
        </h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Max Apps Per Tenant (Free Plan)</label>
            <input
              type="number"
              value={Number(settings.max_apps_per_tenant?.value || 5)}
              onChange={(e) => updateSetting('max_apps_per_tenant', parseInt(e.target.value) || 5)}
              disabled={!isSuperAdmin}
              min={1}
              max={100}
              className="w-full max-w-xs rounded-lg border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground">
              {settings.max_apps_per_tenant?.description || 'Maximum applications per tenant on free plan'}
            </p>
          </div>
        </div>
      </div>

      {/* Rate Limits */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Rate Limits
        </h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Notifications Per Minute</label>
            <input
              type="number"
              value={Number(settings.rate_limit_notifications?.value || 1000)}
              onChange={(e) => updateSetting('rate_limit_notifications', parseInt(e.target.value) || 1000)}
              disabled={!isSuperAdmin}
              min={10}
              max={100000}
              className="w-full max-w-xs rounded-lg border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground">
              {settings.rate_limit_notifications?.description || 'Default rate limit for notifications per minute'}
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">API Calls Per Minute</label>
            <input
              type="number"
              value={Number(settings.rate_limit_api_calls?.value || 10000)}
              onChange={(e) => updateSetting('rate_limit_api_calls', parseInt(e.target.value) || 10000)}
              disabled={!isSuperAdmin}
              min={100}
              max={1000000}
              className="w-full max-w-xs rounded-lg border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground">
              {settings.rate_limit_api_calls?.description || 'Default rate limit for API calls per minute'}
            </p>
          </div>
        </div>
      </div>

      {/* Data Retention */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Data Retention
        </h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Notification Logs (days)</label>
            <input
              type="number"
              value={Number(settings.retention_days_notifications?.value || 30)}
              onChange={(e) => updateSetting('retention_days_notifications', parseInt(e.target.value) || 30)}
              disabled={!isSuperAdmin}
              min={1}
              max={365}
              className="w-full max-w-xs rounded-lg border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground">
              {settings.retention_days_notifications?.description || 'Days to retain notification logs'}
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Audit Log (days)</label>
            <input
              type="number"
              value={Number(settings.retention_days_audit_log?.value || 90)}
              onChange={(e) => updateSetting('retention_days_audit_log', parseInt(e.target.value) || 90)}
              disabled={!isSuperAdmin}
              min={1}
              max={365}
              className="w-full max-w-xs rounded-lg border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground">
              {settings.retention_days_audit_log?.description || 'Days to retain audit log entries'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
